import { useState, useCallback } from 'react';
import { parseFiles, validateFileSize } from '../../utils/llm-generator/fileParser';

/**
 * Custom Hook für Datei-Upload und -Verarbeitung
 * 
 * Verwaltet:
 * - Hochgeladene Dateien (File-Objekte)
 * - Geparseter Dateiinhalt (Text)
 * - Native File Upload Option
 */
export function useFileUpload() {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileContents, setFileContents] = useState([]);
  const [useNativeFileUpload, setUseNativeFileUpload] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  
  /**
   * Handle file upload event
   * @param {Event} e - File input change event
   * @param {Function} logFn - Optional logging function
   * @returns {Promise<{success: boolean, filesAdded: number, errors: string[]}>}
   */
  const handleFileUpload = useCallback(async (e, logFn = null) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      return { success: false, filesAdded: 0, errors: ['Keine Dateien ausgewählt'] };
    }
    
    setIsProcessing(true);
    setUploadError(null);
    
    // Add files to state
    setUploadedFiles(prevFiles => [...prevFiles, ...files]);
    
    if (logFn) {
      logFn(`Lade ${files.length} Datei(en)...\n`);
    }
    
    try {
      // Validate file sizes (max 10MB each)
      for (const file of files) {
        validateFileSize(file, 10);
      }
      
      // Parse file contents
      const { results, errors } = await parseFiles(files);
      
      const uploadErrors = [];
      
      if (results.length > 0) {
        setFileContents(prev => [...prev, ...results]);
        if (logFn) {
          logFn(`${results.length} Datei(en) erfolgreich geladen\n`);
        }
      }
      
      if (errors.length > 0) {
        const errorMsg = errors.map(e => `${e.file}: ${e.error}`).join('\n');
        uploadErrors.push(errorMsg);
        setUploadError(errorMsg);
      }
      
      setIsProcessing(false);
      return {
        success: results.length > 0,
        filesAdded: results.length,
        errors: uploadErrors
      };
      
    } catch (err) {
      const errorMsg = `Fehler beim Datei-Upload: ${err.message}`;
      setUploadError(errorMsg);
      setIsProcessing(false);
      return {
        success: false,
        filesAdded: 0,
        errors: [errorMsg]
      };
    }
  }, []);
  
  /**
   * Remove a file by index
   */
  const removeFile = useCallback((index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFileContents(prev => prev.filter((_, i) => i !== index));
  }, []);
  
  /**
   * Clear all files
   */
  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
    setFileContents([]);
    setUploadError(null);
  }, []);
  
  /**
   * Get total size of all uploaded files
   */
  const getTotalSize = useCallback(() => {
    return uploadedFiles.reduce((total, file) => total + file.size, 0);
  }, [uploadedFiles]);
  
  /**
   * Get combined content of all files
   */
  const getCombinedContent = useCallback(() => {
    return fileContents.map(f => f.content).join('\n\n---\n\n');
  }, [fileContents]);
  
  return {
    // State
    uploadedFiles,
    fileContents,
    useNativeFileUpload,
    isProcessing,
    uploadError,
    
    // Setters
    setUploadedFiles,
    setFileContents,
    setUseNativeFileUpload,
    
    // Actions
    handleFileUpload,
    removeFile,
    clearFiles,
    
    // Utilities
    getTotalSize,
    getCombinedContent,
    hasFiles: uploadedFiles.length > 0,
    fileCount: uploadedFiles.length
  };
}

export default useFileUpload;
